import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/lib/picker-search";
import { BookOpen, Plus, Filter } from "lucide-react";
import { useState } from "react";
import type { LearningEntry, StrategyNode, Campaign } from "@shared/schema";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

const insightColors: Record<string, "default" | "secondary" | "destructive"> = {
  winner: "default",
  loser: "destructive",
  pattern: "secondary",
};

const funnelLayerLabels: Record<string, string> = {
  top: "Top of Funnel",
  mid: "Mid Funnel",
  bottom: "Bottom of Funnel",
};

export default function LearningsPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [selectedLearning, setSelectedLearning] = useState<LearningEntry | null>(null);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [filterCampaign, setFilterCampaign] = useState<string>("all");
  const [filterFunnel, setFilterFunnel] = useState<string>("all");

  const { data: entries, isLoading } = useQuery<LearningEntry[]>({
    queryKey: [`/api/brands/${brandId}/learnings`],
    enabled: !!brandId,
  });
  const { data: nodes } = useQuery<StrategyNode[]>({
    queryKey: [`/api/brands/${brandId}/strategy-nodes`],
    enabled: !!brandId,
  });
  const { data: campaigns } = useQuery<Campaign[]>({
    queryKey: [`/api/brands/${brandId}/campaigns`],
    enabled: !!brandId,
  });

  const nodeMap = new Map(nodes?.map(n => [n.id, n]) || []);
  const campaignMap = new Map(campaigns?.map(c => [c.id, c]) || []);

  const createMutation = useMutation({
    mutationFn: async (data: Record<string, string>) => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/learnings`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/learnings`] });
      setOpen(false);
      setFormData({});
      toast({ title: "Learning entry created" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const filtered = entries?.filter(entry => {
    if (filterCampaign !== "all" && entry.campaignId !== filterCampaign) return false;
    if (filterFunnel !== "all" && entry.funnelLayer !== filterFunnel) return false;
    return true;
  }) || [];

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap fade-in-up">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="text-learnings-title">Learnings</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Performance insights linked to campaigns and funnel stages</p>
        </div>
        <Button onClick={() => setOpen(true)} className="rounded-full px-5" data-testid="button-create-learning">
          <Plus className="h-4 w-4 mr-1.5" /> Add Learning
        </Button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <SearchableSelect
          value={filterCampaign}
          onValueChange={setFilterCampaign}
          placeholder="Campaign"
          testId="select-filter-campaign"
          triggerClassName="w-[180px]"
          triggerIcon={<Filter className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />}
          options={[
            { value: "all", label: "All Campaigns" },
            ...(campaigns ?? []).map(c => ({ value: c.id, label: c.name })),
          ]}
        />
        <Select value={filterFunnel} onValueChange={setFilterFunnel}>
          <SelectTrigger className="w-[160px]" data-testid="select-filter-funnel">
            <SelectValue placeholder="Funnel Layer" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Funnels</SelectItem>
            <SelectItem value="top">Top of Funnel</SelectItem>
            <SelectItem value="mid">Mid Funnel</SelectItem>
            <SelectItem value="bottom">Bottom of Funnel</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="space-y-3">{[1, 2, 3].map(i => <Skeleton key={i} className="h-24" />)}</div>
      ) : filtered.length > 0 ? (
        <div className="space-y-3">
          {filtered.map(entry => {
            const node = entry.strategyNodeId ? nodeMap.get(entry.strategyNodeId) : null;
            const campaign = entry.campaignId ? campaignMap.get(entry.campaignId) : null;
            return (
              <Card key={entry.id} className="cursor-pointer premium-card" onClick={() => setSelectedLearning(entry)} data-testid={`card-learning-${entry.id}`}>
                <CardContent className="p-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-sm">{entry.title}</h3>
                      {entry.insightType && (
                        <Badge variant={insightColors[entry.insightType] || "secondary"} className="text-xs capitalize">{entry.insightType}</Badge>
                      )}
                      <Badge variant={entry.status === "approved" ? "default" : "secondary"} className="text-xs capitalize">{entry.status}</Badge>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {campaign && (
                        <Badge variant="secondary" className="text-[10px]" data-testid={`badge-campaign-${entry.id}`}>
                          {campaign.name}
                        </Badge>
                      )}
                      {entry.funnelLayer && (
                        <Badge variant="secondary" className="text-[10px]" data-testid={`badge-funnel-${entry.id}`}>
                          {funnelLayerLabels[entry.funnelLayer] || entry.funnelLayer}
                        </Badge>
                      )}
                      {entry.persuasionStage && (
                        <Badge variant="secondary" className="text-[10px] capitalize" data-testid={`badge-persuasion-${entry.id}`}>
                          {entry.persuasionStage}
                        </Badge>
                      )}
                    </div>
                    {node && <p className="text-xs text-muted-foreground">Node: {node.angleTitle}</p>}
                    {entry.summary && <p className="text-xs text-muted-foreground line-clamp-2">{entry.summary}</p>}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <BookOpen className="h-10 w-10 text-muted-foreground mb-3" />
            <h3 className="font-medium mb-1">No learnings yet</h3>
            <p className="text-sm text-muted-foreground">Record insights from your campaign results</p>
          </CardContent>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add Learning</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input value={formData.title || ""} onChange={e => setFormData(p => ({ ...p, title: e.target.value }))} data-testid="input-learning-title" />
            </div>
            <div className="space-y-2">
              <Label>Campaign</Label>
              <SearchableSelect
                value={formData.campaignId || ""}
                onValueChange={v => setFormData(p => ({ ...p, campaignId: v }))}
                placeholder="Select campaign..."
                testId="select-learning-campaign"
                options={(campaigns ?? []).map(c => ({ value: c.id, label: c.name }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Strategy Node</Label>
              <SearchableSelect
                value={formData.strategyNodeId || ""}
                onValueChange={v => setFormData(p => ({ ...p, strategyNodeId: v }))}
                placeholder="Select node..."
                testId="select-learning-node"
                options={(nodes ?? []).map(n => ({ value: n.id, label: n.angleTitle }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Funnel Layer</Label>
                <Select value={formData.funnelLayer || ""} onValueChange={v => setFormData(p => ({ ...p, funnelLayer: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="top">Top of Funnel</SelectItem>
                    <SelectItem value="mid">Mid Funnel</SelectItem>
                    <SelectItem value="bottom">Bottom of Funnel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Persuasion Stage</Label>
                <Select value={formData.persuasionStage || ""} onValueChange={v => setFormData(p => ({ ...p, persuasionStage: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="awareness">Awareness</SelectItem>
                    <SelectItem value="knowledge">Knowledge</SelectItem>
                    <SelectItem value="liking">Liking</SelectItem>
                    <SelectItem value="preference">Preference</SelectItem>
                    <SelectItem value="conviction">Conviction</SelectItem>
                    <SelectItem value="purchase">Purchase</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Insight Type</Label>
              <Select value={formData.insightType || ""} onValueChange={v => setFormData(p => ({ ...p, insightType: v }))}>
                <SelectTrigger><SelectValue placeholder="Select type..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="winner">Winner</SelectItem>
                  <SelectItem value="loser">Loser</SelectItem>
                  <SelectItem value="pattern">Pattern</SelectItem>
                  <SelectItem value="persona">Persona Insight</SelectItem>
                  <SelectItem value="product">Product Insight</SelectItem>
                  <SelectItem value="channel">Channel Insight</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Summary</Label><Textarea value={formData.summary || ""} onChange={e => setFormData(p => ({ ...p, summary: e.target.value }))} /></div>
            <div className="space-y-2"><Label>Why It Worked</Label><Textarea value={formData.whyItWorked || ""} onChange={e => setFormData(p => ({ ...p, whyItWorked: e.target.value }))} /></div>
            <div className="space-y-2"><Label>Why It Failed</Label><Textarea value={formData.whyItFailed || ""} onChange={e => setFormData(p => ({ ...p, whyItFailed: e.target.value }))} /></div>
            <div className="space-y-2"><Label>Next Hypothesis</Label><Textarea value={formData.nextHypothesis || ""} onChange={e => setFormData(p => ({ ...p, nextHypothesis: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => createMutation.mutate(formData)} disabled={!formData.title || createMutation.isPending} data-testid="button-submit-learning">
              {createMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {selectedLearning && (
        <Dialog open={!!selectedLearning} onOpenChange={() => setSelectedLearning(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{selectedLearning.title}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="flex gap-2 flex-wrap">
                {selectedLearning.insightType && <Badge variant={insightColors[selectedLearning.insightType] || "secondary"} className="capitalize">{selectedLearning.insightType}</Badge>}
                <Badge variant={selectedLearning.status === "approved" ? "default" : "secondary"} className="capitalize">{selectedLearning.status}</Badge>
                {selectedLearning.campaignId && campaignMap.get(selectedLearning.campaignId) && (
                  <Badge variant="secondary" className="text-xs">{campaignMap.get(selectedLearning.campaignId)!.name}</Badge>
                )}
                {selectedLearning.funnelLayer && (
                  <Badge variant="secondary" className="text-xs">{funnelLayerLabels[selectedLearning.funnelLayer] || selectedLearning.funnelLayer}</Badge>
                )}
                {selectedLearning.persuasionStage && (
                  <Badge variant="secondary" className="text-xs capitalize">{selectedLearning.persuasionStage}</Badge>
                )}
              </div>
              {selectedLearning.summary && <div><h4 className="text-sm font-medium mb-1">Summary</h4><p className="text-sm text-muted-foreground">{selectedLearning.summary}</p></div>}
              {selectedLearning.whyItWorked && <div><h4 className="text-sm font-medium mb-1">Why It Worked</h4><p className="text-sm text-muted-foreground">{selectedLearning.whyItWorked}</p></div>}
              {selectedLearning.whyItFailed && <div><h4 className="text-sm font-medium mb-1">Why It Failed</h4><p className="text-sm text-muted-foreground">{selectedLearning.whyItFailed}</p></div>}
              {selectedLearning.nextHypothesis && <div><h4 className="text-sm font-medium mb-1">Next Hypothesis</h4><p className="text-sm text-muted-foreground">{selectedLearning.nextHypothesis}</p></div>}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
